import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Job } from '../pages/providers/AppProvider';
import { useApp } from '../pages/providers/AppProvider';
import { normalizeMediaUrl } from '../utils/media';
import { apiClient } from '../lib/api-client';
import { normalizeTier } from '../utils/tier';
import companyParameterService from '../services/companyParameterService';

interface DataContextType {
  jobs: Job[];
  loading: boolean;
  error: string | null;
  filters: {
    location: string;
    type: string;
    category: string;
    salary: string;
    experience: string;
  };
  setFilters: (filters: any) => void;
  refreshJobs: () => Promise<void>;
  savedJobs: string[];
  saveJob: (jobId: string) => void;
  unsaveJob: (jobId: string) => void;
  adminCities: string[];
  adminCitiesByProvince: Record<string, string[]>;
  refreshAdminCities: () => Promise<void>;
  adminCountries: string[];
  refreshAdminCountries: () => Promise<void>;
  adminProvinces: string[];
  refreshAdminProvinces: () => Promise<void>;
  adminIndustries: string[];
  refreshAdminIndustries: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedJobs, setSavedJobs] = useState<string[]>([]);
  const [adminCities, setAdminCities] = useState<string[]>([]);
  const [adminCitiesByProvince, setAdminCitiesByProvince] = useState<Record<string, string[]>>({});
  const [adminCountries, setAdminCountries] = useState<string[]>([]);
  const [adminProvinces, setAdminProvinces] = useState<string[]>([]);
  const [adminIndustries, setAdminIndustries] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    location: '',
    type: '',
    category: '',
    salary: '',
    experience: ''
  });
  const { companies = [] } = useApp();

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError(null);

      const res: any = await apiClient.getJobs({ limit: 100, status: 'active' });
      const dbJobs = Array.isArray(res?.jobs) ? res.jobs : Array.isArray(res) ? res : [];

      const transformedJobs: Job[] = dbJobs.map((dbJob: any) => {
        const rawTier = dbJob.tier || dbJob.type || '';
        const tierCanon = normalizeTier(rawTier) || (dbJob.source === 'newspaper' ? 'newspaper' : (dbJob.employment_type === 'full_time' ? 'premium' : 'latest'));
        const typeFromTier = tierCanon === 'megajob' ? 'mega_job'
          : tierCanon === 'premium' ? 'premium_job'
            : tierCanon === 'prime' ? 'prime_job'
              : tierCanon === 'newspaper' ? 'newspaper_job'
                : 'latest_job';
        return ({
          id: dbJob.id || dbJob._id || Math.random().toString(36).substr(2, 9),
          title: dbJob.title || 'Untitled Job',
          company: dbJob.company?.name || dbJob.company || dbJob.company_id || 'Unknown Company',
          companyId: String(dbJob.company_id || dbJob.companyId || '').trim() || undefined,
          location: dbJob.location || 'Not specified',
          description: dbJob.description || 'No description available',
          desiredCandidate: (dbJob as any).desired_candidate || (dbJob as any).desiredCandidate || '',
          requirements: Array.isArray(dbJob.requirements)
            ? dbJob.requirements
            : (typeof dbJob.requirements === 'string' ? [dbJob.requirements] : []),
          postedDate: dbJob.created_at ? new Date(dbJob.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          publishedDate: dbJob.created_at ? new Date(dbJob.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          deadline: dbJob.deadline
            ? new Date(dbJob.deadline).toISOString().split('T')[0]
            : dbJob.updated_at
              ? new Date(new Date(dbJob.updated_at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
              : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          salary: (() => {
            const currency = String(dbJob.salary_currency || 'NPR');
            const type = String(dbJob.salary_type || '').trim().toLowerCase();
            const pay = String(dbJob.pay_type || '').trim();
            if (dbJob.salary_min && dbJob.salary_max) {
              return `${currency} ${Number(dbJob.salary_min).toLocaleString()} - ${Number(dbJob.salary_max).toLocaleString()}`;
            }
            const s = String(dbJob.salary || dbJob.salary_range || '').trim();
            if (s) return s;
            if (type === 'negotiable' || type.includes('negotiable')) {
              return pay ? `Negotiable (${pay})` : 'Negotiable';
            }
            return 'Negotiable';
          })(),
          type: dbJob.type || typeFromTier,
          tier: tierCanon,
          category: dbJob.category?.name || dbJob.category_id || 'General',
          experience: dbJob.experience_level === 'senior' ? 'Senior Level' : dbJob.experience_level === 'mid' ? 'Mid Level' : (dbJob.experience_level || 'Entry Level'),
          tags: Array.isArray(dbJob.tags) && dbJob.tags.length > 0 ? dbJob.tags : [],
          featured: !!dbJob.featured,
          urgent: !!dbJob.urgent,
          logo: dbJob.company?.logo_url || dbJob.logo || '',
          coverImageUrl: dbJob.cover_image_url || dbJob.coverImageUrl || dbJob.coverImage || null,
          source: dbJob.source || undefined,
        });
      });

      setJobs(transformedJobs);
    } catch (err) {
      console.error('Error loading jobs from backend:', err);
      setJobs([]);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const refreshJobs = async () => {
    await loadJobs();
  };

  const refreshAdminCities = async () => {
    try {
      const items = await companyParameterService.list('location');
      const arr = Array.isArray(items) ? items : [];
      const cityOnly = (val: any): string => {
        const raw = String(val || '').trim();
        if (!raw) return '';
        if (raw.includes(' - ')) {
          const after = raw.split(' - ')[1] || raw;
          const city = after.split(',')[0].trim();
          return city;
        }
        const toks = raw.split(/[,-]/).map(s => s.trim()).filter(Boolean);
        const skipWords = ['nepal', 'province', 'bagmati', 'gandaki', 'koshi', 'lumbini', 'madhesh', 'sudurpashchim'];
        for (const t of toks) {
          const k = t.toLowerCase();
          if (!skipWords.some(w => k.includes(w))) return t;
        }
        return toks[0] || raw;
      };
      const names = arr.map((it: any) => cityOnly(typeof it === 'string' ? it : it?.name)).filter(Boolean) as string[];
      setAdminCities(names);
      const byProv: Record<string, string[]> = {};
      arr.forEach((it: any) => {
        const name = cityOnly(typeof it === 'string' ? it : it?.name);
        const prov = typeof it === 'object' ? (it?.province || it?.prov || it?.parentProvince || '') : '';
        const key = String(prov || '').trim();
        if (!name) return;
        if (key) {
          byProv[key] = byProv[key] || [];
          if (!byProv[key].includes(String(name))) {
            byProv[key].push(String(name));
          }
        }
      });
      setAdminCitiesByProvince(byProv);
    } catch (err) {
      setAdminCities([]);
      setAdminCitiesByProvince({});
    }
  };

  const refreshAdminCountries = async () => {
    try {
      const items = await companyParameterService.list('country');
      const names = Array.isArray(items)
        ? items.map((it: any) => (typeof it === 'string' ? it : it?.name)).filter(Boolean)
        : [];
      setAdminCountries(names as string[]);
    } catch (err) {
      setAdminCountries([]);
    }
  };

  const refreshAdminProvinces = async () => {
    try {
      const items = await companyParameterService.list('province');
      const names = Array.isArray(items)
        ? items.map((it: any) => (typeof it === 'string' ? it : it?.name)).filter(Boolean)
        : [];
      setAdminProvinces(names as string[]);
    } catch (err) {
      setAdminProvinces([]);
    }
  };

  const refreshAdminIndustries = async () => {
    try {
      const items = await companyParameterService.list('industry');
      const names = Array.isArray(items)
        ? items.map((it: any) => (typeof it === 'string' ? it : it?.name)).filter(Boolean)
        : [];
      setAdminIndustries(names as string[]);
    } catch (err) {
      setAdminIndustries([]);
    }
  };

  const saveJob = (jobId: string) => {
    setSavedJobs(prev => {
      if (!prev.includes(jobId)) {
        const updated = [...prev, jobId];
        localStorage.setItem('savedJobs', JSON.stringify(updated));
        return updated;
      }
      return prev;
    });
  };

  const unsaveJob = (jobId: string) => {
    setSavedJobs(prev => {
      const updated = prev.filter(id => id !== jobId);
      localStorage.setItem('savedJobs', JSON.stringify(updated));
      return updated;
    });
  };

  useEffect(() => {
    // Load saved jobs from localStorage
    const saved = localStorage.getItem('savedJobs');
    if (saved) {
      try {
        setSavedJobs(JSON.parse(saved));
      } catch (e) {
        console.error('Error parsing saved jobs:', e);
      }
    }

    // Load initial jobs, then load non-critical parameters to avoid network bottleneck
    loadJobs().then(() => {
      // Small delay to ensure React commits the job data and clears loading state first
      setTimeout(() => {
        refreshAdminCities();
        refreshAdminCountries();
        refreshAdminProvinces();
        refreshAdminIndustries();
      }, 500);
    });
  }, []);

  useEffect(() => {
    if (!Array.isArray(companies) || companies.length === 0) return;
    setJobs(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const byId: Record<string, any> = {};
      const byName: Record<string, any> = {};
      for (const c of companies) {
        const id = String((c as any)?._id || (c as any)?.id || '').trim();
        if (id) byId[id] = c;
        const name = String((c as any)?.name || (c as any)?.company_name || '').trim().toLowerCase();
        if (name) byName[name] = c;
      }
      return prev.map((j: any) => {
        const cid = String((j as any)?.companyId || (j as any)?.company_id || '').trim();
        const cname = String((j as any)?.company || '').trim().toLowerCase();
        let comp = (cid && byId[cid]) || (cname && byName[cname]) || null;
        if (!comp) {
          try {
            const locStr = String((j as any)?.location || '').trim();
            if (locStr && locStr.includes(' - ')) {
              const [bNameRaw, cityRawAndAddr] = locStr.split(' - ');
              const bName = String(bNameRaw || '').trim().toLowerCase();
              const cityRaw = String(cityRawAndAddr || '').trim();
              const city = cityRaw.split(',')[0].trim().toLowerCase();
              for (const c of companies) {
                const branches = Array.isArray((c as any)?.branches) ? (c as any).branches : [];
                const match = branches.find((b: any) => {
                  const bn = String(b?.name || '').trim().toLowerCase();
                  const ct = String(b?.city || '').trim().toLowerCase();
                  return bn === bName && ct === city;
                });
                if (match) { comp = c; break; }
              }
            }
          } catch { }
        }
        if (!comp) return j as Job;
        const nextName = String((comp as any)?.name || (comp as any)?.company_name || (j as any)?.company || '').trim();
        const rawLogo = (comp as any)?.logo_url || (comp as any)?.logoUrl || (comp as any)?.logo || (comp as any)?.profileImage || null;
        const normalizedLogo = normalizeMediaUrl(rawLogo) || (rawLogo || null);
        const nextCompanyId = (j as any)?.companyId || String((comp as any)?._id || (comp as any)?.id || '');
        let nextLocation = (j as any)?.location || '';
        try {
          const locStr = String((j as any)?.location || '').trim();
          if (locStr && locStr.includes(' - ')) {
            const bname = locStr.split(' - ')[0].trim().toLowerCase().replace(/^branch\s+/i, '');
            const branches = Array.isArray((comp as any)?.branches) ? (comp as any).branches : [];
            const br = branches.find((b: any) => String(b?.name || '').trim().toLowerCase() === bname);
            if (br) {
              const city = String(br?.city || '').trim();
              const addr = String(br?.address || '').trim();
              const showAddr = !!addr && addr.toLowerCase() !== city.toLowerCase();
              nextLocation = `${br.name} - ${city}${showAddr ? ', ' + addr : ''}`;
            }
          }
          if (!nextLocation) {
            const rawParts = [
              String((comp as any)?.location || '').trim(),
              String((comp as any)?.address || '').trim(),
              String((comp as any)?.city || '').trim(),
              String((comp as any)?.province || (comp as any)?.state || '').trim(),
              String((comp as any)?.country || '').trim(),
            ].filter(Boolean);
            const tokens: string[] = [];
            const seen = new Set<string>();
            rawParts.flatMap(p => String(p).split(/[,-]/).map(s => s.trim()).filter(Boolean))
              .forEach(tok => { const k = tok.toLowerCase(); if (!seen.has(k)) { seen.add(k); tokens.push(tok); } });
            nextLocation = tokens.join(', ');
          }
        } catch { }
        const changed = nextName !== (j as any)?.company || nextCompanyId !== (j as any)?.companyId || nextLocation !== (j as any)?.location || normalizedLogo !== (j as any)?.logo;
        if (!changed) return j as Job;
        return {
          ...(j as any),
          company: nextName,
          companyId: nextCompanyId,
          logo: normalizedLogo || (j as any)?.logo || '',
          location: nextLocation,
        } as Job;
      });
    });
  }, [companies]);



  const value: DataContextType = {
    jobs,
    loading,
    error,
    filters,
    setFilters,
    refreshJobs,
    savedJobs,
    saveJob,
    unsaveJob,
    adminCities,
    adminCitiesByProvince,
    refreshAdminCities,
    adminCountries,
    refreshAdminCountries,
    adminProvinces,
    refreshAdminProvinces,
    adminIndustries,
    refreshAdminIndustries
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
